import _ from 'lodash';
import { ADVISER_LOGO_FUNCTIONS } from '../constants/user';
import { UserSubscription } from './user-subscription';
import store from '../store';

export const getConnectedAdviserDetail = user =>
  _.get(user, 'advisorDetails', []).find(adviserDetail => adviserDetail.connection);
export const isUserConnected = user => _.get(user, 'userType', '') === 'User' && !!getConnectedAdviserDetail(user);
export const shouldShowUserFirstGuideline = user => user && user.userType === 'User' && !user.firstLoginGuidelineDone;
export const shouldShowAdviserFirstGuideline = user =>
  user && user.userType === 'Adviser' && !user.firstLoginGuidelineDone;
export const shouldShowWelcomeDialog = user => user && !!user.welcomeMessage;
export const getUserCurrency = user => _.get(user, 'currency', 'HKD');
export const isUserType = user => _.get(user, 'userType') === 'User';
export const isAdviserType = user => _.get(user, 'userType') === 'Adviser';
export const formatCampaignCode = code => (code || '').trim().toUpperCase();
export const hasQuickClosingCampaignCode = (user, teamCampaignCodes) =>
  !!_.get(user, 'campaignCodes', [])
    .concat(teamCampaignCodes || [])
    .find(campaignCode =>
      ['DE1123', 'WIN1111', 'ODWIN'].find(code => formatCampaignCode(code) === formatCampaignCode(campaignCode.text))
    );
export const hasCampaignCode = (user, teamCampaignCodes, code) =>
  _.get(user, 'campaignCodes', [])
    .concat(teamCampaignCodes || [])
    .find(campaignCode => formatCampaignCode(campaignCode.text) === formatCampaignCode(code));
export const hasReferralCode = (user, code) =>
  _.get(user, 'campaignCodes', []).find(
    campaignCode => campaignCode.isReferralCode && formatCampaignCode(campaignCode.text) === formatCampaignCode(code)
  );
export const shouldEnableChatGPT = ({ user, teamCampaignCodes, userSubscription }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_GPT') ||
  new UserSubscription(userSubscription).containVaildUltraSubscription();
export const shouldShowFinHome = ({ user, teamCampaignCodes }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'PARTNER_FINHOME');
export const shouldEnableSpeechToText = ({ user, teamCampaignCodes, userSubscription }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_GPT') ||
  new UserSubscription(userSubscription).containVaildUltraSubscription();
export const showShowTeamLibrary = ({ user, teamCampaignCodes }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_TEAMFILE');
export const shouldEnableNFCAutoGen = ({ user, teamCampaignCodes }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_AUTONFC');
export const shouldEnableTeamChatBot = ({ user, teamCampaignCodes, userSubscription }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_TEAMCHATBOT') ||
  new UserSubscription(userSubscription).containVaildUltraSubscription();
export const shouldEnableMedicalReserve = ({ user, teamCampaignCodes }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_MEDICAL_RESERVE');
export const shouldEnableAiPolicy = ({ user, teamCampaignCodes }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_AIPOLICY');
export const shouldEnableLab = ({ user, teamCampaignCodes }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_LAB');

export const shouldEnableAiCoach = ({ user, teamCampaignCodes }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_AICOACH');

/**
 * ClientV2's 招募模式 — the drawer's 業務｜招募 view selector, the recruitment
 * panel behind it, and the 潛在招募 flag switch that reveals the selector.
 *
 * Gated because the mode is still under development, and ClientV2 itself is
 * not: `/clientv2` is a public route, reached by every adviser from the PRO
 * badge on V1's client page (topNavigation.js). Without this, an unfinished
 * feature ships to the whole book the moment the page does.
 *
 * A campaign code rather than an env flag because it can be opened to ONE
 * adviser (`users.campaignCodes`) or to a whole pilot team
 * (`team.campaignCodes`, merged in by functionController) without a deploy,
 * and closed again the same way.
 *
 * NOT gated by it: the 招募 tab, the 💼 row badge and the tab hand-off from
 * 業績. Those mirror V1, which offers them to everyone — withholding them
 * would make ClientV2 the poorer page for an adviser who has neither code.
 */
export const shouldEnableRecruitmentMode = ({ user, teamCampaignCodes }) =>
  !!hasCampaignCode(user, teamCampaignCodes, 'FEATURE_RECRUITMODE');

export const getUserInitial = user => (user?.name ? user.name.charAt(0).toUpperCase() : '');
export const formatPhoneNumber = phoneNumber => (phoneNumber ? phoneNumber.replace(/\+852|\+86|\+886/, '$& ') : '');
export const tagsToTagsString = tags => (tags || []).map(tag => `#${tag}`).join(' ');
export const tagsStringToTags = str =>
  (str || '')
    .trim()
    .split('#')
    .map(tag => tag.trim())
    .filter(tag => !!tag);
export const getUserTimezone = user => {
  const { timeZone } = user || {};
  return timeZone ?? 480;
};

export const getUserLogo = (user, teamLogoUrl, functionName) => {
  const { customLogo, customLogoSettings } = user || {};

  if (!teamLogoUrl && !customLogo) {
    return null;
  }

  if (teamLogoUrl && !customLogo) {
    return teamLogoUrl;
  }

  switch (functionName) {
    case ADVISER_LOGO_FUNCTIONS.LOGIN:
      return teamLogoUrl || null;
    case ADVISER_LOGO_FUNCTIONS.MENU:
      if (customLogo && customLogoSettings?.useInMenu === true) {
        return customLogo;
      }
      return teamLogoUrl || null;
    case ADVISER_LOGO_FUNCTIONS.PDF:
      if (customLogo && customLogoSettings?.useInPDF === true) {
        return customLogo;
      }
      return teamLogoUrl || null;
    case ADVISER_LOGO_FUNCTIONS.LAZY_PACK:
      if (customLogo && customLogoSettings?.useInLazyPack === true) {
        return customLogo;
      }
      return teamLogoUrl || null;
    case ADVISER_LOGO_FUNCTIONS.PROFILE_PLUS:
      if (customLogo && customLogoSettings?.useInProfilePlus === true) {
        return customLogo;
      }
      return teamLogoUrl || null;
    case ADVISER_LOGO_FUNCTIONS.ACCOUNT:
      return customLogo || teamLogoUrl || null;
    default:
      return teamLogoUrl || customLogo || null;
  }
};

export const formatTwoRowsString = str => {
  if (!str) {
    return str;
  }

  if ((str.match(/\n/g) || []).length <= 1) {
    return str;
  }

  let formattedStr = '';
  const splits = str.split('\n').filter(split => !!split.trim());

  for (let i = 0; i < splits.length; i++) {
    if (!splits[i].trim()) {
      continue;
    }

    formattedStr += splits[i];

    if (i < splits.length - 1) {
      if (i === 0) {
        formattedStr += '\n';
      } else {
        formattedStr += ' ';
      }
    }
  }

  return formattedStr;
};

export const getIsChubbUser = () => {
  const user = store.getState().user?.userDetails;
  const teamCampaignCodes = store.getState().functions.campaignCodes;
  const teams = store.getState().team.teams;

  if (!user) {
    return false;
  }
  const chubbTeamShortNames = ['CBB', 'CBBTrainer', 'CBBTest'];
  const isChubbTeam = !!teams?.some(team => chubbTeamShortNames?.includes(team?.teamShortName));
  const isChubbCampaignCode = !!hasCampaignCode(user, teamCampaignCodes, 'ENTCBB');
  return isChubbTeam || isChubbCampaignCode;
};
